import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Clock, Undo2, CheckCircle, AlertCircle, ChevronDown, Loader2, Rocket, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function api(token) {
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
}

const EXAMPLE_COMMANDS = [
  "Add ISSA Personal Training certification to qualifications",
  "Change hero heading to 'Build Your Best Self'",
  "Add CrossFit Level 2 to qualifications with detail about advanced training methods",
  "Update nutrition description to focus on Indian diet planning",
  "Add a new online training feature: 'Monthly Body Composition Analysis'",
  "Change the hero badge text to '20+ Years of Excellence'",
  "Add 'Functional Training' as a new service with description",
  "Update about bio to mention strength and conditioning specialization",
];

export default function AdminAITab({ token }) {
  const [command, setCommand] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api(token).get("/ai/commands");
      setHistory(data);
    } catch {}
  }, [token]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const executeCommand = async () => {
    if (!command.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api(token).post("/ai/command", { command: command.trim() });
      setResult(data);
      if (data.success) {
        toast.success(`Updated ${data.sections_updated.length} section(s) as draft`);
        fetchHistory();
      } else {
        toast.error(data.error || "Command failed");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errMsg = typeof detail === "string" ? detail : (detail ? JSON.stringify(detail) : "AI command failed");
      toast.error(errMsg);
      setResult({ success: false, error: errMsg });
    }
    setLoading(false);
  };

  const publishAll = async () => {
    try {
      const { data } = await api(token).post("/ai/publish-all");
      toast.success(data.message);
      setResult(null);
    } catch {
      toast.error("Publish failed");
    }
  };

  const revertCommand = async (cmdId) => {
    if (!window.confirm("Revert all changes from this command?")) return;
    try {
      const { data } = await api(token).post(`/ai/revert/${cmdId}`);
      toast.success(data.message);
      fetchHistory();
    } catch {
      toast.error("Revert failed");
    }
  };

  const deleteCommand = async (cmdId) => {
    try {
      await api(token).delete(`/ai/commands/${cmdId}`);
      toast.success("Command deleted");
      fetchHistory();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      executeCommand();
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-ai-section">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Sparkles size={20} className="text-gg-red" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-wide">AI Command Center</h2>
      </div>
      <p className="text-zinc-500 text-xs leading-relaxed">
        Type a command in plain English to update your site content. Changes are saved as drafts — review in the Content CMS tab, then publish.
      </p>

      {/* Command Input */}
      <div className="bg-gg-surface border border-white/10 rounded-xl p-4 space-y-3">
        <div className="relative">
          <Textarea
            ref={inputRef}
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., Add ISSA Personal Training certification to qualifications..."
            rows={2}
            className="bg-gg-black border-white/10 text-white rounded-lg pr-12 text-sm resize-none"
            data-testid="ai-command-input"
            disabled={loading}
          />
          <Button
            onClick={executeCommand}
            disabled={loading || !command.trim()}
            size="sm"
            className="absolute bottom-2 right-2 bg-gg-red hover:bg-gg-red-hover text-white rounded-lg h-8 w-8 p-0"
            data-testid="ai-command-send"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </Button>
        </div>

        {/* Example commands */}
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLE_COMMANDS.slice(0, 4).map((ex, i) => (
            <button
              key={i}
              onClick={() => { setCommand(ex); inputRef.current?.focus(); }}
              className="text-[9px] font-heading uppercase tracking-wider text-zinc-600 bg-white/5 px-2.5 py-1 rounded-full hover:bg-gg-red/10 hover:text-gg-red transition-all duration-300"
            >
              {ex.length > 45 ? ex.slice(0, 45) + "..." : ex}
            </button>
          ))}
        </div>
      </div>

      {/* Loading state */}
      <AnimatePresence>
        {loading && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-gg-surface border border-gg-red/20 rounded-xl p-5 flex items-center gap-3">
            <Loader2 size={18} className="text-gg-red animate-spin" />
            <div>
              <p className="text-white text-sm font-semibold">AI is processing your command...</p>
              <p className="text-zinc-500 text-xs mt-0.5">Analyzing current content and generating updates</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`border rounded-xl p-5 space-y-3 ${result.success ? "bg-gg-surface border-green-500/20" : "bg-gg-surface border-red-500/20"}`}
            data-testid="ai-result">
            <div className="flex items-start gap-2">
              {result.success ? <CheckCircle size={16} className="text-green-400 mt-0.5" /> : <AlertCircle size={16} className="text-red-400 mt-0.5" />}
              <div className="flex-1">
                <p className="text-white text-sm font-semibold">{result.success ? result.message : "Command Failed"}</p>
                {result.error && <p className="text-red-400 text-xs mt-1">{result.error}</p>}
                {result.sections_updated?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {result.sections_updated.map((s) => (
                      <span key={s} className="text-[9px] font-heading uppercase tracking-wider bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Preview */}
            {result.preview && Object.keys(result.preview).length > 0 && (
              <div className="bg-gg-black rounded-lg p-3 max-h-48 overflow-y-auto">
                <p className="text-[9px] font-heading uppercase tracking-wider text-zinc-500 mb-2">Preview of changes</p>
                <pre className="text-zinc-400 text-[10px] whitespace-pre-wrap leading-relaxed">{JSON.stringify(result.preview, null, 2).slice(0, 1500)}</pre>
              </div>
            )}

            {result.success && (
              <div className="flex gap-2">
                <Button onClick={publishAll} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg" data-testid="ai-publish-all">
                  <Rocket size={12} className="mr-1" /> Publish All Drafts
                </Button>
                <Button onClick={() => revertCommand(result.id)} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white rounded-lg">
                  <Undo2 size={12} className="mr-1" /> Revert
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Command History */}
      <div>
        <button onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors"
          data-testid="ai-history-toggle">
          <Clock size={12} />
          Command History ({history.length})
          <ChevronDown size={12} className={`transition-transform ${showHistory ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {showHistory && history.length > 0 && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="mt-3 space-y-2 overflow-hidden">
              {history.map((cmd) => (
                <div key={cmd.id} className="bg-gg-surface border border-white/5 rounded-lg p-3 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs truncate">{cmd.command}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-zinc-600 text-[9px]">{new Date(cmd.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      {cmd.sections_updated?.map((s) => (
                        <span key={s} className="text-[8px] font-heading uppercase tracking-wider bg-white/5 text-zinc-500 px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => { setCommand(cmd.command); inputRef.current?.focus(); }}
                      className="text-zinc-600 hover:text-white text-[9px] px-2 py-1 rounded hover:bg-white/5 transition-colors"
                      title="Re-run this command">Re-run</button>
                    <button onClick={() => revertCommand(cmd.id)}
                      className="text-zinc-600 hover:text-red-400 text-[9px] px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
                      title="Revert this command">
                      <Undo2 size={10} />
                    </button>
                    <button onClick={() => deleteCommand(cmd.id)}
                      className="text-zinc-600 hover:text-red-400 text-[9px] px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
                      data-testid={`ai-delete-cmd-${cmd.id}`}
                      title="Delete this command">
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
